import React, { useState, useEffect } from 'react';
import apiService from '../../services/ApiService';
import { formatValueForDisplay, areValuesEqual, castToAppType } from './typeCasting';
import snakeToPascal from '../../tools';
import TailwindModal from '../Tailwind/TailwindModal';
import { cn } from '../../lib/utils';

interface ErpBindingOverview {
    model_data_name: string;
    model_data_type: string;
    field_name: string;
    field_type: string;
    field_value: any;
    erp_value: any;
    field_id: number;
    binding_id: number;
    field_key?: string | null;
}

interface TimingContext {
    start_date: string | null;
    end_date: string | null;
    periodicity: string;
}

interface ErpSyncOverviewModalProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    scenarioId: number;
    scenarioName?: string;
    onSyncComplete?: (fieldId: number, newValue: any, fieldKey?: string | null) => void;
    getTimingContext?: (fieldId: number) => TimingContext | null;
}

const mapFieldType = (fieldType: string): 'str' | 'int' | 'float' | 'datetime' | 'bool' | undefined => {
    switch (fieldType) {
        case 'str':
        case 'string':
            return 'str';
        case 'int':
        case 'integer':
            return 'int';
        case 'float':
        case 'double':
        case 'number':
            return 'float';
        case 'datetime':
        case 'date':
        case 'time_serie_timing':
        case 'customizable_timing_reference':
            return 'datetime';
        case 'bool':
        case 'boolean':
            return 'bool';
        default:
            return undefined;
    }
};

const getEffectiveFieldType = (binding: ErpBindingOverview): 'str' | 'int' | 'float' | 'datetime' | 'bool' | undefined => {
    if (binding.field_key === 'start_date' || binding.field_key === 'end_date') {
        return 'datetime';
    }
    return mapFieldType(binding.field_type);
};

export default function ErpSyncOverviewModal({
    open,
    setOpen,
    scenarioId,
    scenarioName,
    onSyncComplete,
    getTimingContext,
}: ErpSyncOverviewModalProps) {
    const [loading, setLoading] = useState(false);
    const [bindings, setBindings] = useState<ErpBindingOverview[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [syncingIds, setSyncingIds] = useState<Set<number>>(new Set());
    const [syncAllConfirmOpen, setSyncAllConfirmOpen] = useState(false);
    const [syncingAll, setSyncingAll] = useState(false);

    useEffect(() => {
        if (open && scenarioId) {
            loadBindings();
        }
    }, [open, scenarioId]);

    const loadBindings = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await apiService.getScenarioErpBindingsOverview(scenarioId);
            if (response && response.ok) {
                const data = await response.json();
                setBindings(Array.isArray(data) ? data : []);
            } else {
                setError('Failed to load ERP bindings');
            }
        } catch (err) {
            console.error('Failed to load ERP bindings:', err);
            setError('An error occurred while loading ERP bindings');
        }
        setLoading(false);
    };

    const handleClose = () => setOpen(false);

    const isTimingFieldType = (fieldType: string): boolean =>
        fieldType === 'time_serie_timing' || fieldType === 'customizable_timing_reference';

    const getTimingValues = (fieldId: number, fieldKey: string, newValue: string) => {
        const siblingBindings = bindings.filter(b => b.field_id === fieldId && b.field_key && b.field_key !== fieldKey);

        let startDate: string | null = null;
        let endDate: string | null = null;
        let periodicity = 'monthly';

        if (fieldKey === 'start_date') startDate = newValue;
        else if (fieldKey === 'end_date') endDate = newValue;

        for (const sibling of siblingBindings) {
            if (sibling.field_key === 'start_date' && startDate === null) startDate = sibling.field_value;
            else if (sibling.field_key === 'end_date' && endDate === null) endDate = sibling.field_value;
        }

        if ((fieldKey === 'start_date' && endDate === null) || (fieldKey === 'end_date' && startDate === null)) {
            if (getTimingContext) {
                const timingContext = getTimingContext(fieldId);
                if (timingContext) {
                    if (startDate === null && timingContext.start_date) startDate = timingContext.start_date;
                    if (endDate === null && timingContext.end_date) endDate = timingContext.end_date;
                    periodicity = timingContext.periodicity || 'monthly';
                }
            }
        }

        return { start_date: startDate, end_date: endDate, periodicity };
    };

    const handleSyncBinding = async (binding: ErpBindingOverview) => {
        const fieldType = getEffectiveFieldType(binding);
        const castedValue = fieldType ? castToAppType(binding.erp_value, fieldType) : binding.erp_value;

        let apiValue = castedValue;
        if (fieldType === 'datetime' && castedValue && castedValue.format) {
            apiValue = castedValue.format('YYYY-MM-DD');
        }

        setSyncingIds(prev => new Set(prev).add(binding.binding_id));

        try {
            let response;
            if (isTimingFieldType(binding.field_type) && binding.field_key) {
                const timingValues = getTimingValues(binding.field_id, binding.field_key, apiValue);
                response = await apiService.putModelDataTimingValue(
                    binding.field_id,
                    timingValues.start_date,
                    timingValues.end_date,
                    timingValues.periodicity
                );
            } else {
                response = await apiService.putModelDataValue(binding.field_id, apiValue);
            }

            if (response && response.ok) {
                setBindings(prevBindings =>
                    prevBindings.map(b =>
                        b.binding_id === binding.binding_id ? { ...b, field_value: binding.erp_value } : b
                    )
                );
                onSyncComplete?.(binding.field_id, castedValue, binding.field_key);
            } else {
                console.error('Failed to sync binding:', binding.binding_id);
            }
        } catch (err) {
            console.error('Error syncing binding:', err);
        } finally {
            setSyncingIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(binding.binding_id);
                return newSet;
            });
        }
    };

    const getOutOfSyncBindings = () =>
        bindings.filter(b => {
            const fieldType = getEffectiveFieldType(b);
            return !areValuesEqual(b.field_value, b.erp_value, fieldType);
        });

    const handleSyncAll = async () => {
        setSyncAllConfirmOpen(false);
        const outOfSyncBindings = getOutOfSyncBindings();
        if (outOfSyncBindings.length === 0) return;
        setSyncingAll(true);
        for (const binding of outOfSyncBindings) {
            await handleSyncBinding(binding);
        }
        setSyncingAll(false);
    };

    const syncedCount = bindings.filter(b => {
        const fieldType = getEffectiveFieldType(b);
        return areValuesEqual(b.field_value, b.erp_value, fieldType);
    }).length;
    const outOfSyncCount = bindings.length - syncedCount;

    return (
        <TailwindModal
            isOpen={open}
            onClose={handleClose}
            title="ERP Sync Overview"
            size="4xl"
        >
            <div className="flex flex-col gap-4 -mx-6 -my-4 px-6 py-4 max-h-[75vh] overflow-y-auto">
                {scenarioName && (
                    <p className="text-sm text-slate-500">
                        Scenario: <span className="font-semibold text-slate-800">{scenarioName}</span>
                    </p>
                )}

                {loading ? (
                    <div className="flex justify-center py-10">
                        <div className="w-8 h-8 border-2 border-slate-200 border-t-slate-700 rounded-full animate-spin" />
                    </div>
                ) : error ? (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                        {error}
                    </div>
                ) : bindings.length === 0 ? (
                    <div className="flex flex-col items-center gap-2 py-10 bg-slate-50 rounded-lg border border-slate-200 text-center">
                        <span className="material-symbols-outlined text-[40px] text-slate-300">sync_disabled</span>
                        <p className="text-sm text-slate-600">No ERP bindings found for this scenario.</p>
                        <p className="text-xs text-slate-400">Create bindings on individual fields to sync with your ERP.</p>
                    </div>
                ) : (
                    <>
                        {/* Summary bar */}
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-green-300 bg-green-50 text-green-700 text-xs font-semibold">
                                    <span className="material-symbols-outlined text-[14px]">check_circle</span>
                                    {syncedCount} In Sync
                                </span>
                                {outOfSyncCount > 0 && (
                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-amber-300 bg-amber-50 text-amber-700 text-xs font-semibold">
                                        <span className="material-symbols-outlined text-[14px]">warning</span>
                                        {outOfSyncCount} Out of Sync
                                    </span>
                                )}
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-slate-200 bg-slate-50 text-slate-600 text-xs font-semibold">
                                    {bindings.length} Total
                                </span>
                            </div>
                            {outOfSyncCount > 0 && !syncAllConfirmOpen && (
                                <button
                                    onClick={() => setSyncAllConfirmOpen(true)}
                                    disabled={syncingAll || syncingIds.size > 0}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {syncingAll ? (
                                        <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    ) : (
                                        <span className="material-symbols-outlined text-[16px]">sync</span>
                                    )}
                                    {syncingAll ? 'Syncing...' : 'Sync All'}
                                </button>
                            )}
                        </div>

                        {/* Inline sync-all confirmation banner */}
                        {syncAllConfirmOpen && (
                            <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-lg bg-amber-50 border border-amber-200">
                                <p className="text-sm text-amber-800">
                                    Apply ERP values to <span className="font-semibold">{outOfSyncCount}</span> out-of-sync field{outOfSyncCount > 1 ? 's' : ''}?
                                </p>
                                <div className="flex items-center gap-2 shrink-0">
                                    <button
                                        onClick={() => setSyncAllConfirmOpen(false)}
                                        className="px-3 py-1.5 rounded-lg border border-amber-300 text-xs font-medium text-amber-700 hover:bg-amber-100 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleSyncAll}
                                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-semibold hover:bg-slate-700 transition-colors"
                                    >
                                        <span className="material-symbols-outlined text-[14px]">sync</span>
                                        Confirm
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Table */}
                            <div className="rounded-lg border border-slate-200 overflow-hidden">
                                <div className="overflow-auto max-h-[380px]">
                                    <table className="w-full text-sm border-collapse">
                                        <thead>
                                            <tr className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
                                                <th className="px-3 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider w-10">Status</th>
                                                <th className="px-3 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider">Object</th>
                                                <th className="px-3 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider">Field</th>
                                                <th className="px-3 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider">Type</th>
                                                <th className="px-3 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider">Current Value</th>
                                                <th className="px-3 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider">ERP Value</th>
                                                <th className="px-3 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider w-12"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {bindings.map((binding, index) => {
                                                const fieldType = getEffectiveFieldType(binding);
                                                const isSynced = areValuesEqual(binding.field_value, binding.erp_value, fieldType);
                                                const isSyncing = syncingIds.has(binding.binding_id);

                                                return (
                                                    <tr
                                                        key={index}
                                                        className={cn(
                                                            'border-b border-slate-100 last:border-0',
                                                            isSynced
                                                                ? index % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'
                                                                : 'bg-amber-50/60'
                                                        )}
                                                    >
                                                        <td className="px-3 py-2 text-center">
                                                            {isSynced ? (
                                                                <span className="material-symbols-outlined text-[18px] text-green-600">check_circle</span>
                                                            ) : (
                                                                <span className="material-symbols-outlined text-[18px] text-amber-500">warning</span>
                                                            )}
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            <p className="font-semibold text-slate-800 text-xs leading-tight">{binding.model_data_name}</p>
                                                            <p className="text-[11px] text-slate-400 leading-tight">{snakeToPascal(binding.model_data_type)}</p>
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            <p className="text-slate-800 text-xs leading-tight">{snakeToPascal(binding.field_name)}</p>
                                                            {binding.field_key && (
                                                                <p className="text-[11px] text-slate-400 leading-tight">{snakeToPascal(binding.field_key)}</p>
                                                            )}
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600">
                                                                {fieldType ? snakeToPascal(fieldType) : snakeToPascal(binding.field_type)}
                                                            </span>
                                                        </td>
                                                        <td className="px-3 py-2 text-xs text-slate-700 max-w-[140px] truncate">
                                                            {formatValueForDisplay(
                                                                fieldType ? castToAppType(binding.field_value, fieldType) : binding.field_value,
                                                                fieldType
                                                            )}
                                                        </td>
                                                        <td className={cn(
                                                            'px-3 py-2 text-xs max-w-[140px] truncate',
                                                            isSynced ? 'text-slate-700' : 'text-amber-700 font-semibold'
                                                        )}>
                                                            {formatValueForDisplay(
                                                                fieldType ? castToAppType(binding.erp_value, fieldType) : binding.erp_value,
                                                                fieldType
                                                            )}
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            {!isSynced && (
                                                                <button
                                                                    title="Apply ERP value"
                                                                    onClick={() => handleSyncBinding(binding)}
                                                                    disabled={isSyncing || syncingAll}
                                                                    className="h-7 w-7 flex items-center justify-center rounded-full bg-slate-900 text-white hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                                >
                                                                    {isSyncing ? (
                                                                        <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                                                    ) : (
                                                                        <span className="material-symbols-outlined text-[14px]">sync</span>
                                                                    )}
                                                                </button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </TailwindModal>
    );
}
